import React, { useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, Card, CardContent, Grid, Chip, TextField
} from "@mui/material";
import { RestartAlt } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { API_ORIGIN as API_URL } from "../../config/env";
import DateRangeFilter, { buildDateRangeParams, defaultDateRangeFilter } from "../../components/common/DateRangeFilter";
import { AdminAction } from "../../components/admin";

export default function FarmDiaryPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ state: "", district: "" });
  const [dateFilter, setDateFilter] = useState(defaultDateRangeFilter);

  const hasActiveFilters = !!(filters.state || filters.district || dateFilter.range !== defaultDateRangeFilter.range || (dateFilter.range === "custom" && (dateFilter.fromDate !== defaultDateRangeFilter.fromDate || dateFilter.toDate !== defaultDateRangeFilter.toDate)));

  const token = localStorage.getItem("rfis");

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/farm-diary/admin/dashboard`, {
        headers: { rf_token_header123_k: token },
        params: { ...filters, ...buildDateRangeParams(dateFilter) },
      });
      setSummary(res.data.data);
    } catch (err) {
      toast.error("Failed to fetch farm diary dashboard");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDashboard();
  }, [filters, dateFilter]);

  // Process data for display
  const processData = () => {
    if (!summary || !Array.isArray(summary)) return { crops: [], totalExpense: 0, totalIncome: 0 };

    const cropMap = {};
    let totalExpense = 0;
    let totalIncome = 0;

    summary.forEach((item) => {
      const key = item.cropName;
      if (!cropMap[key]) {
        cropMap[key] = { cropName: key, expense: 0, income: 0, entries: 0, state: item.state, district: item.district };
      }
      const amount = parseFloat(item.totalAmount) || 0;
      if (item.entryType === "expense") {
        cropMap[key].expense += amount;
        totalExpense += amount;
      } else {
        cropMap[key].income += amount;
        totalIncome += amount;
      }
      cropMap[key].entries += parseInt(item.entryCount) || 0;
    });

    return { crops: Object.values(cropMap), totalExpense, totalIncome };
  };

  const { crops, totalExpense, totalIncome } = processData();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Farm Diary Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Overview of all user farm expense/income entries across crops and regions.
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 2, mb: 2 }}>
        <TextField
          size="small"
          label="State"
          value={filters.state}
          onChange={(e) => setFilters({ ...filters, state: e.target.value })}
        />
        <TextField
          size="small"
          label="District"
          value={filters.district}
          onChange={(e) => setFilters({ ...filters, district: e.target.value })}
        />
        <DateRangeFilter value={dateFilter} onChange={setDateFilter} />
        <AdminAction
          id="reset-filters-btn"
          variant="outline"
          size="sm"
          startIcon={<RestartAlt />}
          disabled={!hasActiveFilters}
          onClick={() => {
            setFilters({ state: "", district: "" });
            setDateFilter(defaultDateRangeFilter);
          }}
          sx={{
            whiteSpace: "nowrap",
            borderColor: hasActiveFilters ? "rgba(63,122,42,0.4)" : undefined,
            color: hasActiveFilters ? "#3f7a2a" : undefined,
            "&:hover": hasActiveFilters ? { borderColor: "#3f7a2a", background: "rgba(63,122,42,0.06)" } : {},
          }}
        >
          Reset
        </AdminAction>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3, mt: 1 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: "#e8f5e9" }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total Income</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                ₹{totalIncome.toLocaleString("en-IN")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: "#ffebee" }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total Expenses</Typography>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                ₹{totalExpense.toLocaleString("en-IN")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: totalIncome - totalExpense >= 0 ? "#e3f2fd" : "#fff3e0" }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Net Profit/Loss</Typography>
              <Typography variant="h4" fontWeight="bold"
                color={totalIncome - totalExpense >= 0 ? "primary.main" : "warning.main"}>
                ₹{(totalIncome - totalExpense).toLocaleString("en-IN")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Crop Name</strong></TableCell>
              <TableCell><strong>State</strong></TableCell>
              <TableCell><strong>District</strong></TableCell>
              <TableCell><strong>Total Income</strong></TableCell>
              <TableCell><strong>Total Expense</strong></TableCell>
              <TableCell><strong>Net P/L</strong></TableCell>
              <TableCell><strong>Entries</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {crops.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">No farm diary entries found</TableCell>
              </TableRow>
            ) : (
              crops.map((crop, idx) => (
                <TableRow key={idx}>
                  <TableCell>{crop.cropName}</TableCell>
                  <TableCell>{crop.state || "-"}</TableCell>
                  <TableCell>{crop.district || "-"}</TableCell>
                  <TableCell sx={{ color: "green" }}>₹{crop.income.toLocaleString("en-IN")}</TableCell>
                  <TableCell sx={{ color: "red" }}>₹{crop.expense.toLocaleString("en-IN")}</TableCell>
                  <TableCell>
                    <Chip
                      label={`₹${(crop.income - crop.expense).toLocaleString("en-IN")}`}
                      color={crop.income - crop.expense >= 0 ? "success" : "error"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{crop.entries}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
